<template>
  <tbody :class="{ [$style.container]: inContainer }">
    <template v-for="(record, recordIndex) in tableData">
      <tr
        :key="getRowKey(recordIndex)"
        :class="$style.tableRow"
      >
        <td
          v-if="renderActionColumn"
          :class="[$style.tableCell, $style.caretTableCell]"
        >
          <button
            :class="$style.expandButton"
            @click.stop="handleExpandButtonClick(getRowKey(recordIndex))"
          >
            <CollapseCaret
              :class="$style.caret"
              :collapsed="!isExpanded(getRowKey(recordIndex))"
            />
          </button>
        </td>
        <td
          v-for="(column, columnIndex) in columns"
          :key="columnIndex"
          :class="[$style.tableCell, getCellClass(record, column)]"
        >
          <span
            v-if="isBodyComponentAndClickableItemColumn(column, record)"
          >
            <ButtonLink
              :isMatchText="false"
              :text="getRecordValue(record,column)"
              @click="emitCellClick(column, getRecordValue(record, column))"
            />
            <component
              :is="column.bodyComponent"
              v-bind="column.bodyComponentProps"
              :item="record"
            />
          </span>
          <component
            :is="column.bodyComponent"
            v-else-if="column.bodyComponent"
            v-bind="column.bodyComponentProps"
            :item="record"
          />
          <a
            v-else-if="column.clickable && column.clickable(record)"
            :class="[$style.tableCellDataText, $style.tableCellDataLink]"
            @click="emitCellClick(column, getRecordValue(record, column))"
          >
            {{ getRecordValue(record, column) }}
          </a>
          <span
            v-else
            :class="$style.tableCellDataText"
          >
            {{ getRecordValue(record, column) }}
          </span>
        </td>
      </tr>
      <tr
        v-if="isExpanded(getRowKey(recordIndex))"
        :key="`details-${recordIndex}`"
      >
        <td
          :class="$style.tableCell"
          :colspan="columns.length + 1"
        >
          <slot
            :columns="columns"
            name="expandedContent"
            :record="record"
          />
        </td>
      </tr>
    </template>
  </tbody>
</template>
<script>

import { ButtonLink } from '@jumpcloud/ui-components';
import BodyComponentMixin from 'components/Tables/BodyComponentMixin';
import TableMixin from 'components/Tables/TableMixin';

export default {
  name: 'TableBody',

  components: { ButtonLink },

  mixins: [
    BodyComponentMixin,
    TableMixin,
  ],

  props: {
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      expandedIds: [],
    };
  },

  watch: {
    isRefreshing: {
      handler(newValue, oldValue) {
        if (oldValue === true && newValue === false) {
          // Minimize all expanded rows when done refreshing
          this.expandedIds = [];
        }
      },
    },
  },

  methods: {
    emitCellClick(column, value) {
      this.$emit('cellClick', column, value);
    },
    getCellClass(record, column) {
      if (record.id) {
        return `${column.dataField}__${record.id}`;
      }
      return '';
    },
    getRecordValue(record, column) {
      if (typeof column.getDataValue === 'function') {
        return column.getDataValue(record);
      }
      const recordValue = record[column.dataField];
      if (recordValue === null || recordValue === undefined) {
        return '';
      }
      return String(recordValue);
    },
    getRowKey(recordIndex) {
      return `row-${recordIndex}`;
    },
    handleExpandButtonClick(id) {
      const index = this.expandedIds.indexOf(id);
      if (index !== -1) { this.expandedIds.splice(index, 1); } else { this.expandedIds.push(id); }
    },
    isBodyComponentAndClickableItemColumn(column, record) {
      return column.bodyComponent && column.clickable && column.clickable(record);
    },
    isExpanded(id) {
      return this.expandedIds.includes(id);
    },
  },
};
</script>

<style scoped module>
@value (gradientDark1, gradientDark4, gradientLight6) from '~css/colors.css';

.tableCell {
  border-bottom: 1px solid gradientLight6;
  border-spacing: 0;
  padding: 6px 10px !important;
}

.tableCell:first-child {
  border-left: 1px solid gradientLight6;
}

.tableCell:last-child {
  border-right: 1px solid gradientLight6;
}

.tableRow:last-child .tableCell {
  border-bottom: none;
}

.caretTableCell {
  width: 30px;
}

.caret {
  color: gradientDark4;
}

.expandButton {
  background: white;
  padding: 0 0 0 10px;
}

.tableCellDataText {
  word-wrap: break-word;
}

.tableCellDataLink {
  color: gradientDark1;
  cursor: pointer;
}

.tableCellDataLink:hover { color: gradientDark1; }

/* --- Container Styles --- */
.container .tableCell:first-child {
  border-left: none;
}

.container .tableCell:last-child {
  border-right: none;
}
</style>
